import { useState } from 'react'
import { X, Users, Gift, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface DiscordInviteCardProps {
  className?: string
  compact?: boolean
}

const DISCORD_INVITE_URL = 'https://discord.gg/jBH3nkfH6E'
const DISMISS_KEY = 'ecosemantic_discord_invite_dismissed'

export function DiscordInviteCard({ className, compact = false }: DiscordInviteCardProps) {
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem(DISMISS_KEY) === 'true'
  })

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    localStorage.setItem(DISMISS_KEY, 'true')
    setIsDismissed(true)
  }

  const handleJoinDiscord = () => {
    window.open(DISCORD_INVITE_URL, '_blank', 'noopener,noreferrer')
  }

  if (isDismissed) {
    return null
  }

  return (
    <div
      className={cn(
        'relative rounded-lg border bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 dark:from-indigo-500/20 dark:via-purple-500/20 dark:to-pink-500/20',
        compact ? 'p-3' : 'p-4',
        className,
      )}
    >
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-background/50 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-3 h-3 text-muted-foreground" />
      </button>

      {/* Content */}
      <div className={cn('space-y-2', compact ? 'pr-4' : 'pr-6')}>
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          <h4 className={cn('font-medium', compact ? 'text-xs' : 'text-sm')}>Join our Community</h4>
        </div>

        {!compact && (
          <p className="text-xs text-muted-foreground">
            Connect with LCA practitioners, get support, and share your work.
          </p>
        )}

        {/* Promo incentive */}
        <div className="flex items-center gap-1.5 text-xs">
          <Gift className="w-3 h-3 text-success" />
          <span className="text-success-foreground font-medium">
            {compact ? 'Free bonus credits' : 'New members get bonus credits'}
          </span>
        </div>

        {/* Join button */}
        <Button
          size="sm"
          variant="outline"
          onClick={handleJoinDiscord}
          className={cn(
            'w-full mt-2 bg-discord/10 hover:bg-discord/20 border-discord/30 text-discord',
            compact ? 'h-7 text-xs' : 'h-8',
          )}
        >
          <span>Join Discord</span>
          <ExternalLink className="w-3 h-3 ml-1.5" />
        </Button>
      </div>
    </div>
  )
}
